// Three-state upload modal for /my-portfolio.
//   drag-drop  → user drops a screenshot
//   extracting → Haiku vision call to /api/portfolio-extract, scanning-line UX
//   confirm    → editable holdings table → POST /api/portfolio?action=save
//
// Image is read into memory only — never written to disk. The handler base64s
// it for the extract call and discards it once the user clicks Save.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { YT, ytTickerSector } from '@/v2/lib/portfolioTokens'
import { StockLogo } from '@/v2/components/StockLogo'
import { useIsPhone } from '@/v2/hooks/useMediaQuery'

const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const MAX_BYTES = 5 * 1024 * 1024

// ─── small primitives ───────────────────────────────────────────────────────
function Kicker({ children, color, size = 9, ls = '0.18em', style }) {
  return (
    <span style={{
      fontFamily: YT.fontMono, fontSize: size, fontWeight: 700,
      letterSpacing: ls, color: color || YT.inkDim,
      textTransform: 'uppercase', ...style,
    }}>{children}</span>
  )
}

function ModalButton({ label, variant = 'primary', onClick, disabled }) {
  const isPhone = useIsPhone()
  const sty = variant === 'primary'
    ? { background: disabled ? 'rgba(0,255,136,0.4)' : YT.accent, color: YT.accentInk, border: '1px solid transparent' }
    : variant === 'ghost'
      ? { background: 'transparent', color: YT.inkMid, border: `1px solid ${YT.border}` }
      : { background: 'rgba(255,255,255,0.04)', color: YT.ink, border: `1px solid ${YT.border}` }
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        ...sty,
        height: isPhone ? 42 : 32,
        padding: isPhone ? '0 20px' : '0 16px',
        borderRadius: 6,
        fontFamily: YT.fontMono, fontSize: isPhone ? 11.5 : 10.5, fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >{label}</button>
  )
}

// ─── state 1: drag-drop ─────────────────────────────────────────────────────
function DragDropState({ onFile, onCancel, error }) {
  const [hover, setHover] = useState(false)
  const inputRef = useRef(null)
  const isPhone = useIsPhone()

  const handleFiles = (files) => {
    const f = files && files[0]
    if (!f) return
    onFile(f)
  }

  return (
    <>
      <ModalHeader title="Upload portfolio screenshot"
                   subtitle="Drop a screenshot from any brokerage" onCancel={onCancel} />
      <div style={{ padding: isPhone ? 16 : 22 }}>
        <div
          onDragOver={(e) => { e.preventDefault(); setHover(true) }}
          onDragLeave={() => setHover(false)}
          onDrop={(e) => { e.preventDefault(); setHover(false); handleFiles(e.dataTransfer.files) }}
          onClick={() => inputRef.current?.click()}
          style={{
            padding: '52px 28px',
            borderRadius: 10,
            background: hover ? 'rgba(0,255,136,0.08)' : 'rgba(0,255,136,0.04)',
            border: `2px dashed ${hover ? YT.accentEdge : YT.accentDim}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            cursor: 'pointer',
            transition: 'background 120ms, border-color 120ms',
          }}
        >
          <div style={{
            width: 52, height: 52, borderRadius: 12,
            background: 'rgba(0,255,136,0.14)',
            border: `1px solid ${YT.accentDim}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                 stroke={YT.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3v5h5M5 21V3h9l5 5v13H5z" />
              <path d="M9 13l3-3 3 3M12 10v7" />
            </svg>
          </div>
          <div style={{ fontFamily: YT.fontDisplay, fontSize: 14, fontWeight: 600, color: YT.ink }}>
            Drop your screenshot here
          </div>
          <div style={{ fontFamily: YT.fontDisplay, fontSize: 12, color: YT.inkDim }}>
            or <span style={{ color: YT.accent, textDecoration: 'underline' }}>choose a file</span>
          </div>
          <div style={{
            marginTop: 6, display: 'flex', gap: 14,
            fontFamily: YT.fontMono, fontSize: 9, color: YT.inkFaint, letterSpacing: 0.4,
          }}>
            <span>PNG · JPG · WEBP · ≤ 5MB</span>
            <span style={{ width: 1, height: 11, background: YT.borderSoft }} />
            <span>SCHWAB · ROBINHOOD · FIDELITY · E*TRADE · VANGUARD</span>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED.join(',')}
            onChange={(e) => handleFiles(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: '8px 12px', borderRadius: 6,
            background: 'rgba(251,113,133,0.08)', border: `1px solid ${YT.lossDim}`,
            fontFamily: YT.fontDisplay, fontSize: 11.5, color: YT.loss,
          }}>{error}</div>
        )}

        <div style={{
          marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px', borderRadius: 6,
          background: 'rgba(255,255,255,0.02)', border: `1px solid ${YT.borderSoft}`,
        }}>
          <span style={{ color: YT.accent, fontFamily: YT.fontMono, fontSize: 10, marginTop: 1 }}>◆</span>
          <div style={{ fontFamily: YT.fontDisplay, fontSize: 11.5, color: YT.inkMid2, lineHeight: 1.5 }}>
            <strong style={{ color: YT.ink, fontWeight: 600 }}>Extract-and-discard.</strong>
            {' '}Atlas reads the screenshot once, extracts your holdings, then drops the image. Nothing is stored.
          </div>
        </div>
      </div>
      <ModalFooter>
        <ModalButton label="Cancel" variant="ghost" onClick={onCancel} />
      </ModalFooter>
    </>
  )
}

// ─── state 2: extracting (scanning line) ────────────────────────────────────
function ExtractingState({ thumbnail, log }) {
  const isPhone = useIsPhone()
  return (
    <>
      <ModalHeader title="Reading your portfolio…"
                   subtitle="Extracting holdings · won't take long" />
      <div style={{ padding: isPhone ? 16 : 22 }}>
        <div style={{
          position: 'relative', height: 240, borderRadius: 10,
          background: 'rgba(255,255,255,0.018)', border: `1px solid ${YT.border}`,
          overflow: 'hidden',
        }}>
          {thumbnail ? (
            <img src={thumbnail} alt=""
                 style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55 }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, padding: 14,
                          display: 'flex', flexDirection: 'column', gap: 8, opacity: 0.4 }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{ height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.10)' }} />
              ))}
            </div>
          )}
          <div className="y-scan-line" style={{
            position: 'absolute', left: 0, right: 0, height: 2,
            background: `linear-gradient(90deg, transparent 0%, ${YT.accent} 50%, transparent 100%)`,
            boxShadow: `0 0 14px ${YT.accent}`,
            pointerEvents: 'none',
          }} />
        </div>

        <div style={{
          marginTop: 14, padding: '12px 14px', borderRadius: 6,
          background: 'rgba(255,255,255,0.018)', border: `1px solid ${YT.borderSoft}`,
          display: 'flex', flexDirection: 'column', gap: 6,
          fontFamily: YT.fontMono, fontSize: 11, letterSpacing: 0.2,
        }}>
          {log.map((line, i) => (
            <ExtractLogLine key={i} state={line.state}>{line.text}</ExtractLogLine>
          ))}
        </div>

        <div style={{
          marginTop: 12, padding: '8px 12px', borderRadius: 6,
          background: 'rgba(0,255,136,0.04)', border: `1px solid ${YT.accentDim}`,
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: YT.fontDisplay, fontSize: 11, color: YT.inkMid2, lineHeight: 1.5,
        }}>
          <span style={{ color: YT.accent, fontFamily: YT.fontMono, fontSize: 10 }}>◆</span>
          Image read in-memory only. Nothing stored to disk.
        </div>
      </div>
    </>
  )
}

function ExtractLogLine({ state, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {state === 'done' ? (
        <span style={{
          width: 14, height: 14, borderRadius: 3,
          background: 'rgba(0,255,136,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: YT.accent, fontSize: 9, fontWeight: 800,
        }}>✓</span>
      ) : state === 'pending' ? (
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: YT.amber,
          marginLeft: 3, animation: 'yPulse 1.4s ease-in-out infinite',
        }} />
      ) : (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: 'transparent', border: `1px solid ${YT.inkFaint}`, marginLeft: 3,
        }} />
      )}
      <span style={{ flex: 1, color: YT.inkMid }}>{children}</span>
    </div>
  )
}

// ─── state 3: confirmation ──────────────────────────────────────────────────
function ConfirmationState({ initial, suggestedLabel, brokerDetected, warnings, onSave, onCancel, saving, saveError }) {
  const isPhone = useIsPhone()
  const [accountLabel, setAccountLabel] = useState(suggestedLabel || 'My account')
  const [rows, setRows] = useState(initial.map((h, i) => ({ ...h, _key: i })))

  const total = useMemo(() => rows.reduce((s, r) => {
    const v = Number(r.position_value)
    return Number.isFinite(v) ? s + v : s
  }, 0), [rows])

  const updateRow = (key, field, value) => {
    setRows((rs) => rs.map((r) => r._key === key ? { ...r, [field]: value } : r))
  }
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r._key !== key))
  // Accepting a fuzzy-match suggestion: swap the ticker, attach the resolved
  // description, and clear the suggestion + invalid flag so the row stops
  // looking like an error.
  const acceptSuggestion = (key, suggestion) => {
    setRows((rs) => rs.map((r) => r._key === key
      ? { ...r, ticker: suggestion.ticker, description: suggestion.description, valid: true, suggestion: null }
      : r))
  }
  const addRow = () => setRows((rs) => [...rs, {
    _key: Date.now(), ticker: '', shares: '', cost_basis_per_share: '', position_value: '',
  }])

  const canSave = accountLabel.trim().length > 0 &&
    rows.some((r) => r.ticker && Number(r.shares) > 0)

  const submit = () => {
    const holdings = rows
      .map((r) => ({
        ticker: String(r.ticker || '').trim().toUpperCase(),
        shares: Number(r.shares),
        cost_basis_per_share: r.cost_basis_per_share === '' ? null : Number(r.cost_basis_per_share),
        position_value: r.position_value === '' ? null : Number(r.position_value),
      }))
      .filter((h) => h.ticker && Number.isFinite(h.shares) && h.shares > 0)
    onSave({
      account_label: accountLabel.trim(),
      holdings,
      total_value: total || null,
      broker_detected: brokerDetected || null,
    })
  }

  return (
    <>
      <ModalHeader title="Confirm your holdings"
                   subtitle="Edit any cell — we read these and discard the image."
                   onCancel={onCancel} />
      <div style={{ padding: isPhone ? '12px 16px 4px' : '16px 22px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            padding: '4px 9px', borderRadius: 4,
            background: YT.accentSoft, border: `1px solid ${YT.accentDim}`,
            fontFamily: YT.fontMono, fontSize: 9, fontWeight: 700,
            color: YT.accent, letterSpacing: 0.6,
          }}>{warnings && warnings.length ? 'EXTRACTED' : 'DETECTED'}</div>
          <div style={{ flex: 1 }}>
            <Kicker size={8.5} color={YT.inkFaint}>ACCOUNT LABEL</Kicker>
            <input
              value={accountLabel}
              onChange={(e) => setAccountLabel(e.target.value)}
              style={{
                marginTop: 4, width: '100%',
                padding: '6px 10px', borderRadius: 5,
                background: 'rgba(255,255,255,0.04)', border: `1px solid ${YT.border}`,
                color: YT.ink, fontFamily: YT.fontDisplay, fontSize: 13, fontWeight: 600,
                outline: 'none',
              }}
            />
          </div>
        </div>

        {warnings && warnings.length > 0 && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 6,
            background: YT.amberTint, border: `1px solid ${YT.amberDim}`,
            fontFamily: YT.fontDisplay, fontSize: 11, color: YT.amber, lineHeight: 1.5,
          }}>{warnings.join(' · ')}</div>
        )}

        <div style={{
          marginTop: 14, borderRadius: 8, overflow: 'hidden',
          background: 'rgba(255,255,255,0.018)', border: `1px solid ${YT.border}`,
          maxHeight: 360, overflowY: 'auto',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '28px 120px 100px 120px 120px 24px',
            alignItems: 'center', gap: 8,
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.025)',
            borderBottom: `1px solid ${YT.border}`,
            position: 'sticky', top: 0, zIndex: 1,
          }}>
            <span />
            <Kicker size={8.5}>TICKER</Kicker>
            <Kicker size={8.5}>SHARES</Kicker>
            <Kicker size={8.5}>COST / SH</Kicker>
            <Kicker size={8.5}>POSITION</Kicker>
            <span />
          </div>
          {rows.map((r) => (
            <ConfirmationRow key={r._key} row={r}
              onChange={(field, v) => updateRow(r._key, field, v)}
              onRemove={() => removeRow(r._key)}
              onAcceptSuggestion={(s) => acceptSuggestion(r._key, s)} />
          ))}
          <button onClick={addRow} style={{
            width: '100%', padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
            background: 'transparent',
            borderTop: `1px solid ${YT.borderSoft}`, borderLeft: 'none',
            borderRight: 'none', borderBottom: 'none',
            color: YT.inkDim, cursor: 'pointer',
            fontFamily: YT.fontMono, fontSize: 10.5, fontWeight: 700,
            letterSpacing: 0.4, textTransform: 'uppercase',
          }}>
            <span style={{ fontSize: 13, lineHeight: 0.8 }}>＋</span> ADD HOLDING
          </button>
        </div>

        <div style={{
          marginTop: 12, display: 'flex', alignItems: 'center', gap: 14,
          fontFamily: YT.fontMono, fontSize: 10.5, color: YT.inkDim, letterSpacing: 0.3,
        }}>
          <span><strong style={{ color: YT.ink }}>{rows.length} holding{rows.length === 1 ? '' : 's'}</strong></span>
          <span style={{ width: 1, height: 12, background: YT.borderSoft }} />
          <span>total <strong style={{ color: YT.ink }}>{total ? `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</strong></span>
        </div>

        {saveError && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 6,
            background: 'rgba(251,113,133,0.08)', border: `1px solid ${YT.lossDim}`,
            fontFamily: YT.fontDisplay, fontSize: 11.5, color: YT.loss,
          }}>{saveError}</div>
        )}
      </div>

      <ModalFooter>
        <span style={{
          fontFamily: YT.fontDisplay, fontSize: 11, color: YT.inkFaint, fontStyle: 'italic',
        }}>
          Saving will supersede any prior <strong style={{ color: YT.inkMid2, fontStyle: 'normal' }}>{accountLabel}</strong> upload.
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <ModalButton label="Cancel" variant="ghost" onClick={onCancel} />
          <ModalButton label={saving ? 'Saving…' : 'Save portfolio'}
                       onClick={submit} disabled={!canSave || saving} />
        </div>
      </ModalFooter>
    </>
  )
}

function ConfirmationRow({ row, onChange, onRemove, onAcceptSuggestion }) {
  const sec = ytTickerSector((row.ticker || '').toUpperCase())
  const valid = row.valid !== false  // undefined treated as valid (older response)
  // Suppress the colored sector badge when the ticker isn't in Atlas's
  // known map — `ytTickerSector` falls back to a generic "SECTOR" label
  // which is wider than the 120px ticker column and bleeds visually into
  // the SHARES input next door. Leveraged ETFs (MRAM, AMDL, COHX, LABX,
  // DRAM, etc.) all hit this default fallback today.
  const showSectorBadge = sec.label !== 'SECTOR'
  const suggestion = row.suggestion
  return (
    <div style={{
      padding: '10px 12px',
      borderTop: `1px solid ${YT.borderSoft}`,
      background: !valid ? 'rgba(251,191,36,0.04)' : 'transparent',
    }}>
      {/* Subtitle ABOVE the input row — anchored visually to the ticker
          immediately below it, not floating between rows like before. */}
      {row.description && (
        <div style={{
          marginLeft: 36, marginBottom: 6,
          fontFamily: YT.fontDisplay, fontSize: 10.5, color: YT.inkDim, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={row.description}>{row.description}</div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: '28px 120px 100px 120px 120px 24px',
        alignItems: 'center', gap: 8,
      }}>
        <StockLogo ticker={(row.ticker || '').toUpperCase()} size={22} rounded={4} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EditableInput value={row.ticker}
            onChange={(v) => onChange('ticker', v.toUpperCase())}
            align="left" mono />
          {valid && showSectorBadge && (
            <span style={{
              fontFamily: YT.fontMono, fontSize: 8, fontWeight: 700,
              color: sec.hex, letterSpacing: 0.5,
            }}>{sec.label}</span>
          )}
          {!valid && (
            <span title="Not a known ticker" style={{
              fontFamily: YT.fontMono, fontSize: 9, fontWeight: 800,
              color: YT.amber,
            }}>⚠</span>
          )}
        </div>
        <EditableInput value={row.shares} onChange={(v) => onChange('shares', v)} />
        <EditableInput value={row.cost_basis_per_share}
          onChange={(v) => onChange('cost_basis_per_share', v)} prefix="$" />
        <EditableInput value={row.position_value}
          onChange={(v) => onChange('position_value', v)} prefix="$" />
        <button onClick={onRemove} title="Remove row" style={{
          width: 22, height: 22, borderRadius: 4, border: 'none',
          background: 'transparent', color: YT.inkDim, cursor: 'pointer',
          fontFamily: YT.fontMono, fontSize: 14, lineHeight: 1,
        }}>×</button>
      </div>

      {/* Suggestion sits below the input row, anchored to the same container
          so it's unambiguously tied to its row. */}
      {suggestion && (
        <div style={{
          marginLeft: 36, marginTop: 6, display: 'flex',
          alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            fontFamily: YT.fontDisplay, fontSize: 10.5, color: YT.amber,
          }}>
            Not a known ticker. Did you mean{' '}
            <strong style={{ color: YT.ink, fontFamily: YT.fontMono }}>{suggestion.ticker}</strong>
            {suggestion.description ? ` — ${suggestion.description}` : ''}?
          </span>
          <button
            onClick={() => onAcceptSuggestion(suggestion)}
            style={{
              padding: '2px 8px', borderRadius: 4,
              background: YT.amberTint, border: `1px solid ${YT.amberDim}`,
              color: YT.amber, cursor: 'pointer',
              fontFamily: YT.fontMono, fontSize: 9, fontWeight: 800,
              letterSpacing: 0.5,
            }}
          >ACCEPT</button>
        </div>
      )}
    </div>
  )
}

function EditableInput({ value, onChange, prefix = '', align = 'right', mono = true }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '4px 8px', borderRadius: 4,
      background: 'rgba(255,255,255,0.025)', border: `1px solid ${YT.borderSoft}`,
    }}>
      {prefix && <span style={{ color: YT.inkDim, fontFamily: YT.fontMono, fontSize: 11, marginRight: 2 }}>{prefix}</span>}
      <input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1, minWidth: 0,
          background: 'transparent', border: 'none', outline: 'none',
          fontFamily: mono ? YT.fontMono : YT.fontDisplay,
          fontSize: 12, color: YT.ink, fontVariantNumeric: 'tabular-nums',
          textAlign: align,
        }}
      />
    </div>
  )
}

// ─── modal chrome ───────────────────────────────────────────────────────────
function ModalHeader({ title, subtitle, onCancel }) {
  const isPhone = useIsPhone()
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isPhone ? '14px 16px' : '16px 22px',
      borderBottom: `1px solid ${YT.border}`,
    }}>
      <div>
        <Kicker color={YT.accent}>UPLOAD</Kicker>
        <div style={{ marginTop: 3, fontFamily: YT.fontDisplay, fontSize: isPhone ? 15 : 16,
                      fontWeight: 700, color: YT.ink, letterSpacing: -0.2 }}>{title}</div>
        {subtitle && (
          <div style={{ marginTop: 3, fontFamily: YT.fontDisplay, fontSize: 11.5, color: YT.inkDim }}>
            {subtitle}
          </div>
        )}
      </div>
      {onCancel && (
        <button onClick={onCancel} aria-label="Close" style={{
          width: isPhone ? 36 : 28, height: isPhone ? 36 : 28,
          borderRadius: 6, border: 'none',
          background: 'rgba(255,255,255,0.04)', color: YT.inkMid2,
          cursor: 'pointer', fontFamily: YT.fontMono, fontSize: isPhone ? 18 : 15, lineHeight: 1,
          flexShrink: 0,
        }}>×</button>
      )}
    </div>
  )
}

function ModalFooter({ children }) {
  const isPhone = useIsPhone()
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: isPhone ? '12px 16px' : '14px 22px',
      borderTop: `1px solid ${YT.border}`,
      background: 'rgba(255,255,255,0.012)',
      gap: 12,
    }}>{children}</div>
  )
}

// ─── orchestrator ───────────────────────────────────────────────────────────
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      const result = String(r.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

const INITIAL_LOG = [
  { state: 'pending', text: 'Sending screenshot to Atlas vision…' },
  { state: 'queued',  text: 'Reading positions' },
  { state: 'queued',  text: 'Cross-checking tickers' },
]

export function UploadModal({ open, onClose, onSaved, userId, initialAccountLabel }) {
  // ADR-0041 Path B — Clerk JWT attached to both extract + save POSTs.
  const { getToken } = useAuth()
  const [phase, setPhase] = useState('drop')     // 'drop' | 'extracting' | 'confirm'
  const [thumbnail, setThumbnail] = useState(null)
  const [log, setLog] = useState(INITIAL_LOG)
  const [extractError, setExtractError] = useState(null)
  const [extracted, setExtracted] = useState(null)  // { holdings, total_value, warnings, suggestedLabel }
  const [saveError, setSaveError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [exiting, setExiting] = useState(false)   // drives the 200ms scale-out anim on save-success

  // Reset on open.
  useEffect(() => {
    if (!open) return
    setPhase('drop')
    setThumbnail(null)
    setLog(INITIAL_LOG)
    setExtractError(null)
    setExtracted(null)
    setSaveError(null)
    setSaving(false)
    setExiting(false)
  }, [open])

  // ESC to close (only when not mid-save).
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, saving, onClose])

  if (!open) return null

  const handleFile = async (file) => {
    setExtractError(null)
    if (!ALLOWED.includes(file.type)) {
      setExtractError('Image must be PNG, JPG, WEBP, or GIF.')
      return
    }
    if (file.size > MAX_BYTES) {
      setExtractError(`Image too large — max ${Math.round(MAX_BYTES / 1024 / 1024)}MB.`)
      return
    }
    try {
      const thumbUrl = URL.createObjectURL(file)
      setThumbnail(thumbUrl)
    } catch {}
    setPhase('extracting')
    setLog([
      { state: 'pending', text: 'Sending screenshot to Atlas vision…' },
      { state: 'queued',  text: 'Reading positions' },
      { state: 'queued',  text: 'Cross-checking tickers' },
    ])

    try {
      const b64 = await fileToBase64(file)
      // Mid-flight log update so the user feels progress even on a fast extract.
      setLog((l) => [
        { ...l[0], state: 'done', text: 'Screenshot sent to Atlas vision' },
        { ...l[1], state: 'pending' },
        l[2],
      ])
      const token = await getToken()
      const r = await fetch('/api/portfolio-extract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // user_id intentionally NOT sent — endpoint derives it from the
        // verified Clerk JWT (ADR-0041 Path B).
        body: JSON.stringify({ image_base64: b64, image_mime: file.type }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        setExtractError(data?.error || `Extraction failed (HTTP ${r.status})`)
        setPhase('drop')
        return
      }
      if (!Array.isArray(data.holdings) || data.holdings.length === 0) {
        setExtractError('We couldn\'t find any holdings in this image.')
        setPhase('drop')
        return
      }
      setLog((l) => [
        { state: 'done', text: 'Screenshot sent to Atlas vision' },
        { state: 'done', text: `Read ${data.holdings.length} position${data.holdings.length === 1 ? '' : 's'}` },
        { state: 'done', text: 'Cross-checked tickers' },
      ])
      // Default account label preference: broker_detected → existing label →
      // generic. "Schwab" beats "My account" when the vision call recognized
      // the broker; falls back gracefully if not.
      const brokerLabel = data.broker_detected && data.broker_detected !== 'unknown'
        ? data.broker_detected.charAt(0).toUpperCase() + data.broker_detected.slice(1)
        : null
      setExtracted({
        holdings: data.holdings,
        total_value: data.total_value,
        warnings: data.warnings || [],
        broker_detected: data.broker_detected || null,
        suggestedLabel: brokerLabel || initialAccountLabel || 'My account',
      })
      // tiny breath so the "done" state is visible before phase swap
      setTimeout(() => setPhase('confirm'), 320)
    } catch (e) {
      setExtractError(`Extraction failed: ${e.message || 'network error'}`)
      setPhase('drop')
    }
  }

  const handleSave = async ({ account_label, holdings, total_value, broker_detected }) => {
    if (!userId) {
      setSaveError('Not signed in. Refresh and try again.')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getToken()
      const r = await fetch('/api/portfolio?action=save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // user_id intentionally NOT sent — endpoint derives it from the
        // verified Clerk JWT (ADR-0041 Path B).
        body: JSON.stringify({ account_label, holdings, total_value, broker_detected }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        setSaveError(data?.error || `Save failed (HTTP ${r.status})`)
        setSaving(false)
        return
      }
      // Save-success animation: scale modal to 95% + fade out (200ms), then
      // close + bubble up. The page's onSaved handler bumps savedAt to trigger
      // the row stagger animation, so the visual handoff is continuous.
      //
      // try/finally so onClose() ALWAYS runs even if onSaved throws — without
      // this, an exception in the parent's refresh/savedAt handler would leave
      // the modal in exiting=true (opacity 0, scale 0.95, backdrop click
      // disabled because saving never reset) and trap the user behind an
      // invisible overlay with no obvious way out.
      const prefersReduced = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (prefersReduced) {
        try {
          onSaved && onSaved({ upload_id: data.upload_id, count: data.count })
        } finally {
          onClose()
        }
        return
      }
      setExiting(true)
      setTimeout(() => {
        try {
          onSaved && onSaved({ upload_id: data.upload_id, count: data.count })
        } finally {
          onClose()
        }
      }, 210)
    } catch (e) {
      setSaveError(`Save failed: ${e.message || 'network error'}`)
      setSaving(false)
    }
  }

  return (
    <ModalOuter saving={saving} onClose={onClose} phase={phase} exiting={exiting}>
      {phase === 'drop' && (
        <DragDropState onFile={handleFile} onCancel={onClose} error={extractError} />
      )}
      {phase === 'extracting' && (
        <ExtractingState thumbnail={thumbnail} log={log} />
      )}
      {phase === 'confirm' && extracted && (
        <ConfirmationState
          initial={extracted.holdings}
          suggestedLabel={extracted.suggestedLabel}
          brokerDetected={extracted.broker_detected}
          warnings={extracted.warnings}
          onSave={handleSave}
          onCancel={onClose}
          saving={saving}
          saveError={saveError}
        />
      )}
    </ModalOuter>
  )
}

// Split out so the outer phone-vs-desktop padding logic is contained
// to one place rather than peppered through the inline JSX above.
function ModalOuter({ saving, onClose, phase, exiting, children }) {
  const isPhone = useIsPhone()
  return (
    <div
      onClick={() => !saving && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: isPhone ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isPhone ? 8 : 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative', width: phase === 'confirm' ? 720 : 640, maxWidth: '100%',
          // Cap modal height to viewport so it never extends past the bottom
          // edge — without this, a Schwab upload with 14 holdings pushed
          // Save Portfolio off-screen and Ben had to scroll the OUTSIDE of
          // the modal to find it. With maxHeight + overflowY:auto, the rows
          // scroll INSIDE the modal and Save/Cancel stay visible.
          maxHeight: isPhone ? 'calc(100vh - 16px)' : 'calc(100vh - 40px)',
          display: 'flex', flexDirection: 'column',
          // Bottom-sheet feel on phone (alignItems: flex-end above): square
          // off the lower corners so the modal hugs the device edge.
          borderRadius: isPhone ? '14px 14px 0 0' : 14,
          // Inner overflowY:auto so if total content (header + warnings +
          // rows + footer) exceeds the modal cap on a small viewport, the
          // modal contents scroll instead of pushing the Save button off
          // the bottom edge of the screen.
          overflow: 'hidden auto',
          background: 'linear-gradient(180deg, #0d121e 0%, #0a0e1a 100%)',
          border: `1px solid ${YT.borderStrong}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,255,136,0.05)',
          // Save-success: scale to 95% + fade to 0 over 200ms — number-tick on
          // DailySummary picks up where this leaves off for a continuous handoff.
          transform: exiting ? 'scale(0.95)' : 'scale(1)',
          opacity: exiting ? 0 : 1,
          transition: 'transform 200ms cubic-bezier(.4,0,.2,1), opacity 200ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}
